import c from 'classnames';
import React, { useMemo, useState } from 'react';
import {
    CustomProduct,
    FrequencyOrder,
    PageDesignSettings,
    PageSettings,
    PaymentFlowType,
    Plan,
    PricingPage,
    Product,
} from '../types/pricingPage';
import { PaypalConfig } from '../types/settings';
import { StripeTier } from '../types/stripeProduct';
import { getBackgroundGradient } from '../utils/color';
import { formatAmount, formatBillingPeriod, formatFrequency, formatPrice } from '../utils/currency';
import { Corner, getBorderClassName, getShadowClassName } from '../utils/features';
import { sortFeatures } from '../utils/plans';
import { isCustomProduct, isMonthly, isStripePrice, isYearly } from '../utils/products';
import FeatureIconImage from './FeatureIconImage';
import ProductImage from './ProductImage';
import RadioTabs, { RADIO_TAB_ACTIVE_CLASS, RadioTabItem } from './RadioTabs';
import PoweredBy from './PoweredBy';
import Stripe from 'stripe';

const frequencySortOrderLowHigh: { [key: string]: number } = {
    day: 1,
    week: 2,
    month: 3,
    '3month': 4,
    year: 5,
};
const frequencySortOrderHighLow: { [key: string]: number } = {
    day: 5,
    week: 4,
    month: 3,
    '3month': 2,
    year: 1,
};

const isString = (item: string | undefined): item is string => {
    return !!item;
};
const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
export const frequencyToTitle = (frequency: string, count: number, settings: PageSettings) => {
    if (frequency === 'year' && !!settings.customWording.yearlyAbbreviation) {
        return settings.customWording.yearlyAbbreviation;
    } else if (frequency === 'month' && count === 3) {
        return settings.customWording.quarterlyAbbreviation || 'Quarterly';
    } else if (frequency === 'month' && !!settings.customWording.monthlyAbbreviation) {
        return settings.customWording.monthlyAbbreviation;
    } else if (frequency === 'week' && !!settings.customWording.weeklyAbbreviation) {
        return settings.customWording.weeklyAbbreviation;
    } else {
        return capitalize(frequency + 'ly');
    }
};
export const frequencyToLabel = (frequency?: string, count?: number) => `${(count || 0) > 1 ? count : ''}${frequency}`;

const isMiddle = (size: number, index: number) => index === Math.floor(size / 2);
const isLast = (size: number, index: number) => index === size - 1;

export const getPayButtonClassName = (pageId: string) => `payButton${pageId}`;
export const getPayLinkClassName = (pageId: string) => `payLink${pageId}`;

const getPromotedPlanKeys = (
    plans: PlanWithTier[],
    settings: PageSettings,
    designSettings: PageDesignSettings
): string[] => {
    const isPromoted = (plan: Plan, idx: number, plansToCheck: PlanWithTier[]) => {
        if (designSettings.mostPopular.stripeProductId) {
            return plan.stripeProductId === designSettings.mostPopular.stripeProductId;
        }

        return isMiddle(plansToCheck.length, idx);
    };
    if (!settings.monthlyYearlyToggle) {
        return plans.filter((_plan, idx) => isPromoted(_plan, idx, plans)).map((p) => p.key);
    } else {
        const monthlyPlans = plans.filter((plan) => isStripePrice(plan.product) && isMonthly(plan.product));
        const monthlyPromotedPlans = monthlyPlans
            .filter((_plan, idx) => isPromoted(_plan, idx, monthlyPlans))
            .map((p) => p.id);

        const yearlyPlans = plans.filter((plan) => isStripePrice(plan.product) && isYearly(plan.product));
        const yearlyPromotedPlans = yearlyPlans
            .filter((_plan, idx) => isPromoted(_plan, idx, yearlyPlans))
            .map((p) => p.key);

        return [...monthlyPromotedPlans, ...yearlyPromotedPlans] || [];
    }
};

interface PlanWithTier extends Plan {
    tier?: StripeTier;
    key: string;
}

interface CustomAction {
    url: string;
    options?: { openInNewTab: boolean };
}

interface Props {
    page: PricingPage;
    emptyContent: (frequency: string) => React.ReactNode;
    emptyFeature?: React.ReactNode;
    baseUrl?: string;
    disabled?: boolean;
    centered?: boolean;
    poweredBy?: boolean;
    hideCustomCSS?: boolean;
    customAction?: CustomAction;
    paypalConfig?: PaypalConfig;
    currency?: string; // Optional: specify user currency
    disableFreePlans?: boolean;
}

export default function PricingPageComponent({
    page,
    emptyContent,
    emptyFeature = null,
    baseUrl,
    disabled = false,
    centered = true,
    poweredBy = false,
    hideCustomCSS = false,
    customAction,
    paypalConfig,
    currency,
    disableFreePlans = false,
}: Props) {
    const { id, plans, settings, designSettings } = page;
    const [frequency, setFrequency] = useState(settings.defaultFrequency || 'month');

    const allPlans = plans.reduce<PlanWithTier[]>((accumulator, currentPlan) => {
        const { product } = currentPlan;
        if (isStripePrice(product) && product.tiers?.length > 0) {
            product.tiers.forEach((tier) => {
                accumulator.push({ ...currentPlan, key: `${currentPlan.id}-${tier.up_to}`, tier });
            });
        } else {
            accumulator.push({ ...currentPlan, key: currentPlan.id });
        }
        return accumulator;
    }, []);
    const activePlanKeys = (
        page.settings.monthlyYearlyToggle
            ? allPlans.filter((plan) =>
                  isStripePrice(plan.product)
                      ? frequencyToLabel(plan.product.recurring?.interval, plan.product.recurring?.interval_count) ===
                        frequency
                      : plan.key
              )
            : allPlans
    ).map((plan) => plan.key);
    const plansByFrequency = allPlans.reduce<{ [key: string]: string[] }>((accumulator, currentPlan) => {
        const frequency = isStripePrice(currentPlan.product) ? currentPlan.product.recurring?.interval || '' : '';
        if (accumulator[frequency]) {
            accumulator[frequency].unshift(currentPlan.key);
        } else {
            accumulator[frequency] = [currentPlan.key];
        }
        return accumulator;
    }, {});

    const frequenciesByCount = allPlans
        .map((plan) => plan.product)
        .filter(isStripePrice)
        .reduce<{ [key: string]: Set<number> }>((prev, curr) => {
            const key = curr.recurring?.interval;
            if (isString(key)) {
                if (prev[key]) {
                    prev[key].add(curr.recurring?.interval_count || 1);
                } else {
                    prev[key] = new Set([curr.recurring?.interval_count || 1]);
                }
            }
            return prev;
        }, {});

    const frequencySortOrder =
        (settings.frequencyOrder || FrequencyOrder.LOW_HIGH) === FrequencyOrder.LOW_HIGH
            ? frequencySortOrderLowHigh
            : frequencySortOrderHighLow;

    const frequencies: RadioTabItem[] = Object.keys(frequenciesByCount)
        .flatMap((frequency) =>
            Array.from(frequenciesByCount[frequency]).map((count) => ({
                id: frequencyToLabel(frequency, count),
                label: frequencyToTitle(frequency, count, settings),
                value: frequencyToLabel(frequency, count),
            }))
        )
        .sort((a, b) => ((frequencySortOrder[a.id] || 99) < (frequencySortOrder[b.id] || 99) ? -1 : 1));

    const anyAreSubscribed = page.settings.planPicker.enabled && allPlans.some((plan) => plan.currentUserSubscribed);
    const promotedPlanKeys: string[] = getPromotedPlanKeys(allPlans, settings, designSettings);
    const base64Id = typeof btoa === 'function' ? btoa(id) : require('btoa')(id);
    const showPromotedFrequency =
        settings.monthlyYearlyToggle && settings.promotedFrequency && settings.promotedFrequencyWording;
    const backgroundGradient = useMemo(
        () => ({
            backgroundImage: page.designSettings.buttonGradient
                ? getBackgroundGradient(page.designSettings.backgroundColor)
                : 'none',
            backgroundColor: page.designSettings.backgroundColor,
        }),
        [page.designSettings]
    );

    const companySkipPoweredBy = (page.features || []).includes('disablePoweredBy');

    return (
        <>
            {page.designSettings.font && (
                <>
                    <link
                        rel="stylesheet"
                        href={`https://fonts.googleapis.com/css?family=${page.designSettings.font}`}
                    />
                    <style
                        type="text/css"
                        dangerouslySetInnerHTML={{
                            __html: `.${base64Id}-wrapper{ font-family: '${page.designSettings.font}'; }`,
                        }}
                    />
                </>
            )}
            {page.designSettings.customCSS && !hideCustomCSS && (
                <style type="text/css" dangerouslySetInnerHTML={{ __html: page.designSettings.customCSS }}></style>
            )}
            <style type="text/css">
                {`.${RADIO_TAB_ACTIVE_CLASS} {
                    background-image: ${backgroundGradient.backgroundImage};
                    background-color: ${backgroundGradient.backgroundColor};
                }.pricewell-frequency-wrapper {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                
                .pricewell-frequency-wrapper::-webkit-scrollbar {
                    display: none;
                }`}
            </style>
            <div
                id={`${page.id}-wrapper`}
                data-mode={page.status === 'ACTIVE' ? 'live' : 'test'}
                data-font={page.designSettings.font}
                className={`pricewell-wrapper grid grid-cols-1 gap-2 text-left text-black ${base64Id}-wrapper`}
                data-paypal-client-id={
                    page.status === 'ACTIVE' ? paypalConfig?.liveClientId : paypalConfig?.testClientId
                }
            >
                <header className={c('pricewell-header flex flex-col', { 'items-center': centered })}>
                    {settings.monthlyYearlyToggle && (
                        <div className="pricewell-frequency-wrapper pt-8 flex flex-col md:flex-row justify-center items-center space-x-2 space-y-2 max-w-full overflow-x-scroll">
                            {showPromotedFrequency && (
                                <span
                                    className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full border border-green-400 self-end md:self-auto"
                                    style={{ opacity: 0 }}
                                >
                                    {settings.promotedFrequencyWording}
                                </span>
                            )}
                            <form id={`toggle${id}`} className="pricewell-frequency-toggles max-w-full">
                                <RadioTabs
                                    name={`toggle${id}`}
                                    items={frequencies}
                                    value={frequency}
                                    onChange={setFrequency}
                                />
                            </form>
                            {showPromotedFrequency && (
                                <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full border border-green-400 self-end md:self-auto">
                                    {settings.promotedFrequencyWording}
                                </span>
                            )}
                        </div>
                    )}
                </header>
                {activePlanKeys.length === 0 && <>{emptyContent(frequency)}</>}
                <section
                    className={c('pricewell-plans flex flex-wrap w-full md:px-10', {
                        'place-content-center': centered,
                        'pointer-events-none': disabled,
                        'flex-col sm:flex-row': !designSettings.disableFlexCol,
                    })}
                >
                    {allPlans.map((plan, idx) => {
                        const promoted = settings.promotedPlan && promotedPlanKeys.includes(plan.key);
                        return (
                            <PricingPlan
                                key={plan.key}
                                plan={plan}
                                promoted={promoted}
                                page={page}
                                emptyFeature={emptyFeature}
                                baseUrl={baseUrl}
                                disabled={disabled}
                                hidden={!activePlanKeys.includes(plan.key)}
                                upgradeOptions={{
                                    showUpgradeDowngrade:
                                        anyAreSubscribed && plan.paymentFlow.type === PaymentFlowType.CHARGE_NOW,
                                    upgradeWording: settings.customWording?.upgrade,
                                }}
                                isSubscribedToAny={anyAreSubscribed}
                                showCurrentPlan={anyAreSubscribed}
                                poweredBy={
                                    !companySkipPoweredBy &&
                                    poweredBy &&
                                    (isCustomProduct(plan.product)
                                        ? isLast(allPlans.length, idx)
                                        : settings.monthlyYearlyToggle
                                        ? isStripePrice(plan.product) &&
                                          plansByFrequency[plan.product.recurring?.interval || '']?.length > 0 &&
                                          plansByFrequency[plan.product.recurring?.interval || ''][0] === plan.key &&
                                          !isCustomProduct(allPlans[allPlans.length - 1].product)
                                        : isLast(allPlans.length, idx))
                                }
                                customAction={customAction}
                                backgroundGradient={backgroundGradient}
                                currency={currency}
                                disableFreePlans={disableFreePlans}
                            />
                        );
                    })}
                </section>
            </div>
        </>
    );
}

interface UpgradeOptions {
    showUpgradeDowngrade: boolean;
    upgradeWording?: string;
}

interface PricingPlanProps {
    plan: PlanWithTier;
    page: PricingPage;
    promoted: boolean;
    emptyFeature?: React.ReactNode;
    baseUrl?: string;
    disabled?: boolean;
    hidden?: boolean;
    poweredBy?: boolean;
    upgradeOptions?: UpgradeOptions;
    showCurrentPlan?: boolean;
    customAction?: CustomAction;
    backgroundGradient: any; // ts bug can't use CSSProperties
    currency?: string;
    disableFreePlans: boolean;
    isSubscribedToAny: boolean;
}

export function PricingPlan({
    plan,
    page,
    promoted,
    emptyFeature,
    baseUrl,
    disabled = false,
    hidden = false,
    poweredBy = false,
    upgradeOptions = { showUpgradeDowngrade: false },
    showCurrentPlan = false,
    customAction,
    backgroundGradient,
    currency,
    disableFreePlans,
    isSubscribedToAny,
}: PricingPlanProps) {
    const { product, tier, headlinePrice, headlinePriceMonthly, overridePrice } = plan;
    const allProducts = page.plans.map((p) => p.product);
    const monthlyEquivalent = getMonthlyEquivalent(product, allProducts);
    const { settings, designSettings, plans } = page;
    const somePlansHaveDescription = !plans.every((p) => !!p.description) && plans.some((p) => !!p.description);
    const somePlansHaveImages = plans.some(
        (p) => isStripePrice(p.product) && (p.product?.product?.images?.length ?? 0) > 0
    );

    const isFreePlan = isStripePrice(plan.product) && plan.product.unit_amount === 0;

    const hiddenForPlanPicker =
        (disableFreePlans && isFreePlan) ||
        (showCurrentPlan && page.settings.planPicker.disableZeroPrice && isFreePlan);

    if (hiddenForPlanPicker) {
        return <></>;
    }

    if (!product && page.status === 'ACTIVE') {
        console.log(`PriceWell: Missing product`);
        return <></>;
    }

    return (
        <>
            <form
                className={c(
                    `pricewell-plan px-6 py-6 lg:px-4 max-w-sm relative flex-1 mx-auto sm:mx-0 w-full plan${page.id}`,
                    {
                        'transition duration-150 ease-in transform hover:scale-110': designSettings.animatePlanOnHover,
                        'pointer-events-none': disabled,
                        hidden,
                    }
                )}
                data-type={
                    isStripePrice(product)
                        ? frequencyToLabel(product.recurring?.interval, product.recurring?.interval_count)
                        : ''
                }
            >
                <div
                    className={c(
                        'pricewell-plan-header h-full px-4 py-6 border bg-white flex flex-col relative',
                        getBorderClassName(designSettings.borderRadius),
                        getShadowClassName(designSettings.shadow),
                        { 'border-2': promoted && designSettings.mostPopular.enabled }
                    )}
                    style={{
                        borderColor:
                            promoted && designSettings.mostPopular.enabled ? designSettings.backgroundColor : undefined,
                    }}
                >
                    {promoted && designSettings.mostPopular.enabled && (
                        <span
                            className={c('text-white px-3 py-1 tracking-widest text-xs absolute right-0 top-0', {
                                [getBorderClassName(designSettings.borderRadius, Corner.BOTTOMLEFT)]:
                                    designSettings.borderRadius !== 'none',
                                [getBorderClassName(designSettings.borderRadius, Corner.TOPRIGHT)]:
                                    designSettings.borderRadius !== 'none',
                            })}
                            style={{ backgroundColor: designSettings.backgroundColor }}
                        >
                            {(designSettings.mostPopular.wording || 'Popular').toUpperCase()}
                        </span>
                    )}
                    {!product && <h3 className="pricewell-plan-name tracking-widest">Missing Product: {plan.name}</h3>}
                    {product && (
                        <>
                            {!designSettings.hideProductImages && (
                                <ProductImage
                                    product={plan.product}
                                    fillIfMissing={somePlansHaveImages}
                                    className="pricewell-plan-image w-10 h-10 mb-4 ml-auto"
                                />
                            )}
                            <h3 className="pricewell-plan-name tracking-widest">{plan.name}</h3>
                            <h2
                                className={c(
                                    'pricewell-plan-price flex items-center justify-start mt-2 mb-4 font-bold leading-none text-left',
                                    {
                                        'text-3xl lg:text-6xl': isStripePrice(product),
                                        'text-xl lg:text-4xl': isCustomProduct(product) && !!product.price.customLabel,
                                    }
                                )}
                                style={{ color: designSettings.backgroundColor }}
                            >
                                {!!headlinePrice && !headlinePriceMonthly && (
                                    <span className="pricewell-plan-price-discount line-through text-base">
                                        {formatAmount({
                                            amount: headlinePrice,
                                            currency: currency || (isStripePrice(product) && product.currency) || 'usd',
                                            interval: isStripePrice(product)
                                                ? (product.recurring?.interval as any) || 'month'
                                                : 'month',
                                            denomination: 'dollars',
                                        })}
                                    </span>
                                )}
                                {headlinePriceMonthly && monthlyEquivalent && (
                                    <span className="pricewell-plan-price-discount line-through text-base">
                                        {formatPrice(monthlyEquivalent, settings, tier, currency)}
                                    </span>
                                )}
                                <span className="pricewell-plan-price-amount">
                                    {!!overridePrice
                                        ? formatAmount({
                                              amount: overridePrice,
                                              currency:
                                                  currency || (isStripePrice(product) && product.currency) || 'usd',
                                              interval: isStripePrice(product)
                                                  ? (product.recurring?.interval as any) || 'month'
                                                  : 'month',
                                              denomination: 'dollars',
                                          })
                                        : formatPrice(product, settings, tier, currency)}
                                </span>
                                <span className="pricewell-plan-frequency ml-1 text-base text-gray-600">
                                    {formatFrequency(product, settings)} <br />{' '}
                                    <span className="text-xs">{formatBillingPeriod(product, settings)}</span>
                                </span>
                            </h2>
                            {tier && (
                                <p
                                    className={c(
                                        'pricewell-plan-description mb-4 text-base leading-relaxed overflow-hidden',
                                        {
                                            'mt-4': isCustomProduct(product),
                                        }
                                    )}
                                >
                                    {settings?.customWording?.upToXUsers || 'Up to'} {tier.up_to || '∞'}{' '}
                                    {settings?.customWording?.users || 'Users'}
                                </p>
                            )}
                            <p
                                className={c(
                                    'pricewell-plan-description mb-4 text-base leading-relaxed overflow-hidden',
                                    {
                                        'mt-4': isCustomProduct(product),
                                        'h-12 overflow-hidden': somePlansHaveDescription,
                                    }
                                )}
                            >
                                {plan.description || ' '}
                            </p>
                            {designSettings.layout.button === 'top' && (
                                <div className="pricewell-plan-action pb-4">
                                    <div className="flex-col space-y-2">
                                        <ActionButton
                                            page={page}
                                            pageSettings={page.settings}
                                            pageDesignSettings={page.designSettings}
                                            plan={plan}
                                            promoted={promoted}
                                            upgradeOptions={upgradeOptions}
                                            showCurrentPlan={showCurrentPlan}
                                            isSubscribedToAny={isSubscribedToAny}
                                            customerPortalId={page.customerPortalId}
                                            disabled={
                                                upgradeOptions.showUpgradeDowngrade &&
                                                page.settings.planPicker.disableOneOff &&
                                                plan.productType === 'one-off'
                                            }
                                            customAction={customAction}
                                            backgroundGradient={backgroundGradient}
                                        />
                                        {!!product.paypalPlanId && (
                                            <div id={`paypal-button-container-${product.paypalPlanId}`}></div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <Features
                                product={product}
                                plans={plans}
                                designSettings={designSettings}
                                emptyFeature={emptyFeature}
                                baseUrl={baseUrl}
                            />

                            <Addons addons={plan.addons} />

                            {designSettings.layout.button === 'bottom' && (
                                <div className="pricewell-plan-action mt-4 pt-2 flex-grow flex items-end">
                                    <div className="flex-col space-y-2">
                                        <ActionButton
                                            page={page}
                                            pageSettings={page.settings}
                                            pageDesignSettings={page.designSettings}
                                            plan={plan}
                                            promoted={promoted}
                                            upgradeOptions={upgradeOptions}
                                            showCurrentPlan={showCurrentPlan}
                                            isSubscribedToAny={isSubscribedToAny}
                                            customerPortalId={page.customerPortalId}
                                            disabled={
                                                upgradeOptions.showUpgradeDowngrade &&
                                                page.settings.planPicker.disableOneOff &&
                                                plan.productType === 'one-off'
                                            }
                                            customAction={customAction}
                                            backgroundGradient={backgroundGradient}
                                        />
                                        {!!product.paypalPlanId && (
                                            <div id={`paypal-button-container-${product.paypalPlanId}`}></div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
                {poweredBy && <PoweredBy campaignSource="pricing-table" campaignId={page.id} />}
            </form>
        </>
    );
}

function getMonthlyEquivalent(product: Product, allProducts: Product[]) {
    if (isStripePrice(product)) {
        const monthly = allProducts.find(
            (p) =>
                isStripePrice(p) &&
                p.recurring?.interval === 'month' &&
                p.recurring?.interval_count === product.recurring?.interval_count &&
                product.id !== p.id
        );
        return monthly;
    }
    return undefined;
}

function Addons(props: { addons: Plan['addons'] }) {
    if (!props.addons || props.addons.length === 0) {
        return null;
    }
    return (
        <>
            <div className="flex items-center">
                <div className="flex-1 border-t-2 border-gray-200"></div>
                <span className="px-3 text-gray-500 bg-white">Add-ons</span>
                <div className="flex-1 border-t-2 border-gray-200"></div>
            </div>
            {props.addons
                .filter((addon) => addon.product)
                .map((addon) => (
                    <label
                        className="pricewell-addon flex flex-col sm:flex-row items-center mb-2 text-gray-600 flex space-x-2"
                        key={addon.stripePriceId}
                    >
                        {addon.adjustableQuantity && (
                            <select
                                name="addons"
                                className="py-1 px-2 mr-2 transition duration-100 ease-in-out border rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed text-black placeholder-gray-400 bg-white border-gray-300 focus:border-blue-500"
                            >
                                <option>-</option>
                                {Array.from(Array(Number(addon.maxQuantity) || 5).keys()).map((i) => (
                                    <option key={i} value={`${addon.stripePriceId}-${i + 1}`}>
                                        {i + 1}
                                    </option>
                                ))}
                            </select>
                        )}
                        {!addon.adjustableQuantity && (
                            <input
                                type="checkbox"
                                name="addons"
                                value={addon.stripePriceId}
                                className="h-5 w-5 form-checkbox rounded border-gray-300 text-gray-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-offset-0 focus:ring-blue-200 focus:ring-opacity-50 mr-2"
                            />
                        )}
                        {addon.product && (
                            <>
                                Add {addon.description || addon.product.product.name} -{' '}
                                <span className="font-bold">{formatPrice(addon.product)}</span>
                            </>
                        )}
                    </label>
                ))}
        </>
    );
}

function Features({
    plans,
    product,
    designSettings: { featureIcon, subduedIcon },
    emptyFeature,
    baseUrl,
}: {
    plans?: Plan[];
    product: Product;
    designSettings: PageDesignSettings;
    emptyFeature: React.ReactNode;
    baseUrl?: string;
}) {
    const features = plans
        ?.flatMap((plan) =>
            plan.stripePriceId === product.id || plan.product.id === product.id
                ? plan.features
                : plan.features.filter((feature) => feature.allPlans)
        )
        .filter((feature) => feature.name);

    if (features && features.length > 0) {
        return (
            <>
                {features
                    .filter((feature) => feature.visible)
                    .sort(sortFeatures)
                    .map((feature) => (
                        <p
                            className={c('pricewell-plan-feature flex items-center mb-2 text-gray-600', {
                                'line-through': feature.subdued,
                            })}
                            key={feature.id}
                        >
                            {!feature.subdued && featureIcon !== 'none' && (
                                <FeatureIconImage featureIcon={featureIcon} baseUrl={baseUrl} />
                            )}
                            {feature.subdued && subduedIcon !== 'none' && (
                                <FeatureIconImage featureIcon={subduedIcon} baseUrl={baseUrl} />
                            )}
                            {feature.name}
                        </p>
                    ))}
                {features.length < 4 &&
                    Array(4 - features.length)
                        .fill(' ')
                        .map((_, idx) => (
                            <p className="flex items-center mb-2 text-gray-600" key={`blank-${idx}`}>
                                &nbsp;
                            </p>
                        ))}
            </>
        );
    } else if (isCustomProduct(product)) {
        return (
            <>
                <p className="flex items-center mb-2 text-gray-600">&nbsp;</p>
                <p className="flex items-center mb-2 text-gray-600">&nbsp;</p>
                <p className="flex items-center mb-2 text-gray-600">&nbsp;</p>
                <p className="flex items-center mb-2 text-gray-600">&nbsp;</p>
            </>
        );
    }

    return <>{emptyFeature}</>;
}

function ActionButton(props: {
    page: PricingPage;
    promoted: boolean;
    plan: PlanWithTier;
    pageSettings: PageSettings;
    pageDesignSettings: PageDesignSettings;
    upgradeOptions: UpgradeOptions;
    isSubscribedToAny: boolean;
    showCurrentPlan: boolean;
    customerPortalId?: string;
    disabled: boolean;
    customAction?: CustomAction;
    backgroundGradient: any; // ts bug can't use CSSProperties
}) {
    const pageId = props.page.id;
    const { id: planId, designSettings, paymentFlow, product, tier, subscriptionSettings } = props.plan;

    const isSubscribed = !!props.plan.currentUserSubscribed && props.showCurrentPlan;
    const isProductCustom = isCustomProduct(product);
    const needsButtonStyles = isProductCustom || !isSubscribed;
    const className = c(
        'px-4 sm:px-8 py-2 font-semibold whitespace-nowrap text-sm sm:text-base',
        getShadowClassName(props.pageDesignSettings.shadow),
        {
            'transition duration-500 ease-in-out transform border rounded-lg focus:shadow-outline focus:outline-none':
                needsButtonStyles,
            'text-white bg-gradient-to-r from-blue-700 hover:from-blue-600 to-blue-600 hover:to-blue-700':
                needsButtonStyles && props.promoted,
            'bg-white text-black hover:text-white hover:bg-black hover:border-black':
                needsButtonStyles && !props.promoted,
        }
    );
    const shouldShowUpgrade = props.upgradeOptions.showUpgradeDowngrade && props.plan.productType === 'recurring';
    const upgradeWording =
        (subscriptionSettings.trialDays || 0) > 0 &&
        !(props.isSubscribedToAny && !!subscriptionSettings.trialSettings?.oncePerCustomer)
            ? 'Start Trial'
            : props.upgradeOptions.upgradeWording || 'Switch Plan';
    const buttonText = shouldShowUpgrade ? upgradeWording : designSettings.buttonText;

    if (props.disabled) {
        return null;
    } else if (isValidUrl(props.customAction?.url) || isProductCustom || props.plan.callToActionUrl) {
        const url = (
            (isValidUrl(props.customAction?.url)
                ? props.customAction?.url
                : isProductCustom
                ? (product as CustomProduct).callToActionUrl
                : props.plan.callToActionUrl) || ''
        ).replace(/{{PLAN_ID}}/g, planId);
        return url ? (
            <a
                href={url}
                className={c(className, getPayLinkClassName(pageId))}
                style={props.promoted ? props.backgroundGradient : undefined}
                rel="noopener noreferrer"
                target={
                    props.plan.linkOptions.openInNewTab || props.customAction?.options?.openInNewTab
                        ? '_blank'
                        : undefined
                }
            >
                {buttonText}
            </a>
        ) : null;
    } else if (isSubscribed) {
        return <span className={className}>{getCurrentPlanWording(props.plan, props.page)}</span>;
    } else if (paymentFlow.type === PaymentFlowType.CHARGE_LATER && paymentFlow.actionUrl) {
        return (
            <a
                href={paymentFlow.actionUrl}
                className={c(className, getPayLinkClassName(pageId))}
                style={props.promoted ? props.backgroundGradient : undefined}
                id={planId}
                rel="noopener noreferrer"
            >
                {buttonText}
            </a>
        );
    }

    return (
        <input
            type="button"
            className={c('cursor-pointer', className, getPayButtonClassName(props.page.id))}
            style={props.promoted ? props.backgroundGradient : undefined}
            id={planId}
            data-quantity={tier?.up_to}
            data-upgrade={shouldShowUpgrade && !isSubscribed}
            data-upgrade-confirm={!!props.pageSettings?.planPicker?.confirmChanges}
            // data-upgrade-portal={ not using customer portal for upgrades as no one is using it and we need a manual flow for our use
            //     shouldShowUpgrade /*TODO: make this configurable with a setting */ ? props.customerPortalId : undefined
            // }
            data-paypal-plan={product.paypalPlanId}
            data-success-url={paymentFlow.paymentSuccessUrl}
            value={buttonText}
        />
    );
}

function isValidUrl(url?: string) {
    if (!url || (url || '').toLowerCase().includes('buy.stripe.com')) {
        return false;
    }
    return true;
}

function getDaysBetween(date1: Date, date2: Date) {
    const diff = date1.getTime() - date2.getTime();
    return Math.round(diff / (1000 * 3600 * 24));
}

function getCancellationWording(cancelAt: Date) {
    const daysBetween = getDaysBetween(cancelAt, new Date());
    if (daysBetween < 0) {
        return 'Cancelled';
    }
    if (daysBetween === 0) {
        return 'Cancels today';
    }
    if (daysBetween === 1) {
        return 'Cancels tomorrow';
    }
    return `Cancels in ${daysBetween} days`;
}

function getCurrentPlanWording(plan: Plan, page: PricingPage) {
    if (!!plan.currentUserSubscription?.cancel_at) {
        return getCancellationWording(new Date(plan.currentUserSubscription.cancel_at * 1000));
    }

    if (isStripeSubscriptionSchedule(plan.currentUserSubscription?.schedule)) {
        if (plan.currentUserSubscription?.schedule?.phases?.length! > 1) {
            const nextPhase = plan.currentUserSubscription?.schedule?.phases[1]!;
            const upgradeToStripePriceId = nextPhase.items[0]!.price;
            const daysBetween = getDaysBetween(new Date(nextPhase.start_date * 1000), new Date());
            if (daysBetween <= 0) {
                return 'Cancels today';
            } else {
                const matchingPlan = page.plans.find((p) => p.stripePriceId === upgradeToStripePriceId);
                return `Changes ${matchingPlan ? 'to ' + matchingPlan.name : ''} in ${daysBetween} days`;
            }
        }
    }

    if (plan.currentUserSubscription?.status === 'trialing' && plan.currentUserSubscription.trial_end) {
        const daysBetween = getDaysBetween(new Date(plan.currentUserSubscription.trial_end * 1000), new Date());
        if (daysBetween < 0) {
            return 'Current Plan';
        }
        if (daysBetween === 0) {
            return 'Trial ends today';
        }
        if (daysBetween === 1) {
            return 'Trial ends tomorrow';
        }
        return `Trial ends in ${daysBetween} days`;
    }

    return 'Current Plan';
}

function isStripeSubscriptionSchedule(
    subscription?: Stripe.Subscription['schedule']
): subscription is Stripe.SubscriptionSchedule {
    return (subscription as Stripe.SubscriptionSchedule)?.id !== undefined;
}
